import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/ClientLayout';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, FileText, MoreHorizontal, Edit, Trash2, Send, Check, X, Link as LinkIcon, Copy } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import axios from 'axios';

const EstimatesPage = () => {
  const { token, API } = useAuth();
  const [estimates, setEstimates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [portalLinkDialog, setPortalLinkDialog] = useState(false);
  const [portalLink, setPortalLink] = useState('');
  const [formData, setFormData] = useState({
    customer_id: '',
    property_id: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0 }],
    notes: '',
    valid_until: ''
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const [estimatesRes, customersRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/estimates${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setEstimates(estimatesRes.data.estimates);
      setCustomers(customersRes.data.customers);
      setProperties(propertiesRes.data.properties);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.customer_id || !formData.property_id) {
      toast.error('Customer and property are required');
      return;
    }
    if (formData.line_items.length === 0 || !formData.line_items[0].description) {
      toast.error('At least one line item is required');
      return;
    }

    try {
      await axios.post(`${API}/estimates`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Estimate created successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create estimate');
    }
  };

  const handleStatusChange = async (estimateId, newStatus) => {
    try {
      await axios.put(`${API}/estimates/${estimateId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Estimate ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update estimate');
    }
  };

  const handleConvert = async (estimateId) => {
    try {
      await axios.post(`${API}/estimates/${estimateId}/convert`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Estimate converted to job');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert estimate');
    }
  };

  const handleSendToCustomer = async (estimateId) => {
    try {
      const response = await axios.post(`${API}/estimates/${estimateId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const baseUrl = window.location.origin;
      const fullLink = `${baseUrl}${response.data.portal_url}`;
      setPortalLink(fullLink);
      setPortalLinkDialog(true);
      fetchData();
      toast.success('Customer portal link generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send estimate');
    }
  };

  const copyPortalLink = () => {
    navigator.clipboard.writeText(portalLink);
    toast.success('Link copied to clipboard');
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      property_id: '',
      line_items: [{ description: '', quantity: 1, unit_price: 0 }],
      notes: '',
      valid_until: ''
    });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const removeLineItem = (index) => {
    setFormData({
      ...formData,
      line_items: formData.line_items.filter((_, i) => i !== index)
    });
  };

  const updateLineItem = (index, field, value) => {
    const items = [...formData.line_items];
    items[index][field] = value;
    setFormData({ ...formData, line_items: items });
  };

  const calculateTotal = () => {
    return formData.line_items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || 'Unknown';
  const getPropertyAddress = (id) => properties.find(p => p.id === id)?.address || 'Unknown';
  const filteredProperties = properties.filter(p => p.customer_id === formData.customer_id);

  const filteredEstimates = estimates.filter(e =>
    getCustomerName(e.customer_id).toLowerCase().includes(searchQuery.toLowerCase()) ||
    getPropertyAddress(e.property_id).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const variants = {
      draft: 'outline',
      sent: 'secondary',
      approved: 'default',
      declined: 'destructive',
      converted: 'default'
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  return (
    <ClientLayout title="Estimates">
      <Card className="border-gray-200">
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search estimates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-estimates-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="add-estimate-btn">
                  <Plus className="h-4 w-4 mr-2" /> New Estimate
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Estimate</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Customer *</Label>
                      <Select 
                        value={formData.customer_id} 
                        onValueChange={(value) => setFormData({ ...formData, customer_id: value, property_id: '' })}
                      >
                        <SelectTrigger data-testid="estimate-customer-select">
                          <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map((customer) => (
                            <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Property *</Label>
                      <Select 
                        value={formData.property_id} 
                        onValueChange={(value) => setFormData({ ...formData, property_id: value })}
                        disabled={!formData.customer_id}
                      >
                        <SelectTrigger data-testid="estimate-property-select">
                          <SelectValue placeholder="Select property" />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredProperties.map((property) => (
                            <SelectItem key={property.id} value={property.id}>{property.address}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Line Items</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                        <Plus className="h-4 w-4 mr-1" /> Add Item
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {formData.line_items.map((item, index) => (
                        <div key={index} className="flex gap-2 items-start">
                          <Input
                            placeholder="Description"
                            value={item.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            className="flex-1"
                          />
                          <Input
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                            className="w-20"
                          />
                          <Input
                            type="number"
                            placeholder="Price"
                            value={item.unit_price}
                            onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="w-24"
                          />
                          <span className="w-20 text-right py-2 text-sm font-medium">
                            ${(item.quantity * item.unit_price).toFixed(2)}
                          </span>
                          {formData.line_items.length > 1 && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(index)}>
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-end mt-2 pt-2 border-t">
                      <span className="font-semibold">Total: ${calculateTotal().toFixed(2)}</span>
                    </div>
                  </div>

                  <div>
                    <Label>Valid Until</Label>
                    <Input
                      type="date"
                      value={formData.valid_until}
                      onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes..."
                      rows={2}
                    />
                  </div>

                  <Button className="w-full bg-primary hover:bg-primary/90" onClick={handleSubmit} data-testid="save-estimate-btn">
                    Create Estimate
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredEstimates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No estimates found</p>
              <Button variant="outline" onClick={() => setDialogOpen(true)}>
                Create Your First Estimate
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Customer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Property</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Total</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEstimates.map((estimate) => (
                    <tr key={estimate.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`estimate-${estimate.id}`}>
                      <td className="py-3 px-4 font-medium text-gray-900">{getCustomerName(estimate.customer_id)}</td>
                      <td className="py-3 px-4 text-gray-600 text-sm">{getPropertyAddress(estimate.property_id)}</td>
                      <td className="py-3 px-4 font-medium text-gray-900">${estimate.total?.toLocaleString()}</td>
                      <td className="py-3 px-4">{getStatusBadge(estimate.status)}</td>
                      <td className="py-3 px-4 text-gray-600 text-sm">{new Date(estimate.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {estimate.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleSendToCustomer(estimate.id)}>
                                <Send className="h-4 w-4 mr-2" /> Send to Customer
                              </DropdownMenuItem>
                            )}
                            {(estimate.status === 'sent' || estimate.portal_token) && (
                              <DropdownMenuItem onClick={() => {
                                const link = `${window.location.origin}/portal/estimate/${estimate.portal_token}`;
                                setPortalLink(link);
                                setPortalLinkDialog(true);
                              }}>
                                <LinkIcon className="h-4 w-4 mr-2" /> View Portal Link
                              </DropdownMenuItem>
                            )}
                            {estimate.status === 'sent' && (
                              <>
                                <DropdownMenuItem onClick={() => handleStatusChange(estimate.id, 'approved')}>
                                  <Check className="h-4 w-4 mr-2" /> Mark Approved
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(estimate.id, 'declined')}>
                                  <X className="h-4 w-4 mr-2" /> Mark Declined
                                </DropdownMenuItem>
                              </>
                            )}
                            {estimate.status === 'approved' && (
                              <DropdownMenuItem onClick={() => handleConvert(estimate.id)}>
                                <Check className="h-4 w-4 mr-2" /> Convert to Job
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Portal Link Dialog */}
      <Dialog open={portalLinkDialog} onOpenChange={setPortalLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Customer Portal Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-gray-600">
              Share this link with your customer so they can view and approve the estimate online.
            </p>
            <div className="flex gap-2">
              <Input 
                value={portalLink} 
                readOnly 
                className="flex-1 text-sm"
                data-testid="portal-link-input"
              />
              <Button onClick={copyPortalLink} variant="outline" data-testid="copy-link-btn">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setPortalLinkDialog(false)}>
                Close
              </Button>
              <Button 
                onClick={() => window.open(portalLink, '_blank')}
                className="bg-primary hover:bg-primary/90"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Open Portal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ClientLayout>
  );
};

export default EstimatesPage;
